import React, { useState, useEffect, useMemo } from "react";
import PropTypes from 'prop-types';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow,
  Checkbox,
  FormControlLabel
} from "@material-ui/core";
import { REQUEST_STATUSES } from "../../../shared/constants/RequestStatuses";
import { useSelector } from "react-redux";
import ReactSelect from "react-select";

import "./DeliveryRestrictionsReviewSection.css";
import { getRestrictionComparer } from "../../../shared/services/DeliveryRestrictionService";

/**
 * DeliveryRestrictionsReviewSection
 * 
 * Renders a table of all delivery restrictions on the project.
 * Shows which restrictions were requested for removal and allows PS users to approve/reject removal.
 * 
 * @param {Array} requestedRestrictionIDs - Array of requested restriction IDs
 * @param {Array} currentRestrictionIDs - Array of current restriction IDs on the project
 * @param {Array} currentRestrictionIDsFromRequest - Array of restriction IDs that were on the project at the time of the request. We only care about this if the request is closed.
 * @param {Function} onSelectRestrictions - Callback when restrictions are selected: (selectedRestrictionIds) => void
 * @param {Boolean} canReviewRestrictions - Whether the user can make selections (the request is currently open and we're moving it to complete)
 * @param {Boolean} historicMode - Whether the request is already closed (and we're just viewing it, not editing it)
 */
const DeliveryRestrictionsReviewSection = ({
  requestedRestrictionIDs,
  currentRestrictionIDs,
  currentRestrictionIDsFromRequest,
  onSelectRestrictions,
  canReviewRestrictions,
  historicMode
}) => {
  const availableRestrictions = useSelector(state => state.lenderRequestReducer.removeDeliveryRestriction.availableRestrictions);

  const availableRestrictionsMap = useMemo(
    () => new Map(availableRestrictions?.map(r => [r.restrictionId, r])),
    [availableRestrictions]
  );
  const requestedIdSet = useMemo(
    () => new Set(requestedRestrictionIDs), 
    [requestedRestrictionIDs]
  );
  
  const currentRestrictionOptions = useMemo(() => {
    if (availableRestrictionsMap?.size) {
      const currentIDs = (historicMode && currentRestrictionIDsFromRequest)
        ? currentRestrictionIDsFromRequest
        : currentRestrictionIDs;
  
      const currentOptions = currentIDs?.map(reqId => {
        const restrictionObj = availableRestrictionsMap.get(reqId);
        const label = restrictionObj?.restrictionName || restrictionObj?.restrictionShortName || "-";

        return {
          value: reqId,
          label: restrictionObj?.restrictionName || restrictionObj?.restrictionShortName || "-",
          shortName: restrictionObj?.restrictionShortName || "-"
        };
      }).sort(getRestrictionComparer());

      // console.log("currentRestrictionOptions", currentOptions);
      return currentOptions;
    }    
    return undefined;
  }, [historicMode, currentRestrictionIDs, currentRestrictionIDsFromRequest, availableRestrictionsMap]);

  const [approvedIdSet, setApprovedIdSet] = useState(new Set());

  useEffect(() => {
    if (requestedIdSet.size) {
      const initialApprovedSet = new Set(requestedIdSet);
      setApprovedIdSet(initialApprovedSet);
    }
  }, [requestedIdSet]);

  const handleDecisionChange = (restrictionId, isApproved) => {
    setApprovedIdSet((prevApprovedIdSet) => {
      const newSet = new Set(prevApprovedIdSet);

      if (isApproved) { newSet.add(restrictionId); }
      else { newSet.delete(restrictionId); }

      if (onSelectRestrictions) {
        onSelectRestrictions(Array.from(newSet));
      }

      return newSet;
    });
  };

  return (
    <>
      <h3 className="request-details-subheader">Delivery Restrictions</h3>
      {!currentRestrictionOptions?.length ? (
        <p className="no-restrictions-message">No delivery restrictions on project.</p>
      ) : (
        <Table className="restrictions-review-table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '25%' }}>Restriction Name</TableCell>
              <TableCell>Short Name</TableCell>
              <TableCell style={{ width: '170px' }}>Removal Requested</TableCell>
              {canReviewRestrictions && <TableCell style={{ width: '170px' }}>Removal Approved</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {currentRestrictionOptions.map((currentOption) => {                
                const isRequested = requestedIdSet.has(currentOption.value);
                const isApproved = approvedIdSet.has(currentOption.value);

                return (
                  <TableRow key={currentOption.value}>
                    <TableCell style={{ width: '25%' }}>{currentOption.label}</TableCell>
                    <TableCell>{currentOption.shortName}</TableCell>
                    <TableCell style={{ width: '170px' }}>
                      <Checkbox
                        checked={isRequested}
                        disabled={true}
                        style={{ color: "rgba(0, 0, 0, 0.26)" }}
                      />
                    </TableCell>
                    {canReviewRestrictions && (
                      <TableCell style={{ width: '170px' }}>
                        <Checkbox
                          checked={isApproved}
                          disabled={!isRequested}
                          onChange={(e) => handleDecisionChange(currentOption.value, e.target.checked)}
                          style={{ color: isRequested ? "#085280" : "rgba(0, 0, 0, 0.26)" }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      )}
    </>
  );
};

DeliveryRestrictionsReviewSection.propTypes = {
  requestedRestrictionIDs: PropTypes.arrayOf(PropTypes.number),
  currentRestrictionIDs: PropTypes.arrayOf(PropTypes.number),
  currentRestrictionIDsFromRequest: PropTypes.arrayOf(PropTypes.number),
  onSelectRestrictions: PropTypes.func,
  canReviewRestrictions: PropTypes.bool,
  historicMode: PropTypes.bool,
};

export default DeliveryRestrictionsReviewSection;
